import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Chalkboard, ArrowLeft, ArrowRight, BellSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import TimelineGrid from '../../components/classroom/TimelineGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBSCRIBED_KEY = (cid) => `classroom_subscribed_${cid}`;
const SUBSCRIBED_EMAIL_KEY = (cid) => `classroom_subscribed_email_${cid}`;
const DISMISSED_KEY = (cid) => `classroom_subscribe_dismissed_${cid}`;
const ANON_KEY = 'classroom_anon_id';

const getOrCreateAnonId = () => {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = `anon_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
};

/**
 * Public parent surface — read-only.
 *   /c/:shareCode                    → workspace info + list of collections
 *   /c/:shareCode/c/:collectionId    → timeline for one collection
 *
 * Backend authorises via ?share_code=… on the read endpoints. No auth needed.
 */
const ParentSharePage = () => {
  const { shareCode, collectionId } = useParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState(null);
  const [collections, setCollections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ws = await axios.get(`${API}/share/classroom/${shareCode}`);
        setWorkspace(ws.data?.workspace || null);
        setCollections(ws.data?.collections || []);
        if (collectionId) {
          const tl = await axios.get(
            `${API}/collections/${collectionId}/uploads?share_code=${encodeURIComponent(shareCode)}`,
          );
          setGroups(tl.data?.groups || []);
          // Track this view (anon, dedup'd to one per day per browser per collection)
          axios
            .post(`${API}/share/classroom/${shareCode}/views`, {
              collection_id: collectionId,
              anon_id: getOrCreateAnonId(),
            })
            .catch(() => {});
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load this link');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareCode, collectionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="text-sm text-gray-400">Link not found.</div>
      </div>
    );
  }

  // Detail (timeline) view
  if (collectionId) {
    const collection = collections.find((c) => c.collection_id === collectionId);
    return (
      <div className="min-h-screen bg-[#0F1014] text-white" data-testid="parent-timeline">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <button
            onClick={() => navigate(`/c/${shareCode}`)}
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
            data-testid="parent-back"
          >
            <ArrowLeft size={14} weight="bold" /> Back to {workspace.name}
          </button>
          <div className="mb-8">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
              {workspace.name}
            </span>
            <h1 className="text-3xl font-black tracking-tight mt-1">{collection?.name || 'Timeline'}</h1>
            <p className="text-sm text-gray-500 mt-1">Read-only view for parents.</p>
          </div>
          <SubscribePrompt shareCode={shareCode} collectionId={collectionId} />
          <TimelineGrid groups={groups} editable={false} />
        </div>
      </div>
    );
  }

  // Index view
  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="parent-share-index">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <Chalkboard size={16} weight="fill" className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
            For Parents
          </span>
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-2">{workspace.name}</h1>
        <p className="text-base text-gray-400 mb-10">
          Tap a collection below to see the latest moments from your child's day.
        </p>

        {/* Story entry points — premium curated surfaces */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <Link
            to={`/c/${shareCode}/today`}
            className="block rounded-2xl px-5 py-4 transition-all hover:scale-[1.005]"
            style={{
              background: 'linear-gradient(135deg, #ffffff, #f3f4f6)',
              color: '#15161B',
            }}
            data-testid="parent-today-story-cta"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 mb-0.5">
                  🎬 Today's Story
                </div>
                <div className="text-base font-semibold">A glimpse from today</div>
                <div className="text-xs text-gray-500 mt-0.5">The day's best moments</div>
              </div>
              <ArrowRight size={18} weight="bold" className="text-gray-700 shrink-0" />
            </div>
          </Link>

          <Link
            to={`/c/${shareCode}/week`}
            className="block rounded-2xl px-5 py-4 transition-all hover:scale-[1.005]"
            style={{
              background: 'linear-gradient(135deg, #ffffff, #e9ecf3)',
              color: '#15161B',
            }}
            data-testid="parent-week-story-cta"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 mb-0.5">
                  📅 This Week
                </div>
                <div className="text-base font-semibold">A week of moments</div>
                <div className="text-xs text-gray-500 mt-0.5">The Sunday recap</div>
              </div>
              <ArrowRight size={18} weight="bold" className="text-gray-700 shrink-0" />
            </div>
          </Link>
        </div>

        {collections.length === 0 ? (
          <div className="text-sm text-gray-500" data-testid="parent-empty">
            Nothing shared yet — check back soon.
          </div>
        ) : (
          <div className="space-y-2">
            {collections.map((c) => (
              <Link
                key={c.collection_id}
                to={`/c/${shareCode}/c/${c.collection_id}`}
                className="w-full text-left rounded-xl px-5 py-4 flex items-center justify-between transition-all hover:bg-white/5 block"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid={`parent-collection-${c.collection_id}`}
              >
                <div>
                  <div className="font-semibold text-white">{c.name}</div>
                  <div className="text-xs text-gray-500">View timeline</div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-gray-500" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentSharePage;

/**
 * Tiny inline card prompting the parent for their email so they get the daily
 * digest. Optional — dismissable. State persisted in localStorage so we don't
 * nag the same person twice.
 */
const SubscribePrompt = ({ shareCode, collectionId }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(() =>
    typeof window !== 'undefined' && Boolean(localStorage.getItem(SUBSCRIBED_KEY(collectionId))),
  );
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== 'undefined' && Boolean(localStorage.getItem(DISMISSED_KEY(collectionId))),
  );

  if (done) {
    return (
      <div
        className="rounded-xl px-4 py-3 mb-6 flex items-center gap-2 text-xs"
        style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}
        data-testid="parent-subscribe-confirmed"
      >
        <BellSimple size={12} weight="fill" className="text-emerald-400" />
        <span className="text-emerald-300 font-bold uppercase tracking-[0.14em]">Subscribed</span>
        <span className="text-gray-400">— You'll get an email when new photos land.</span>
      </div>
    );
  }
  if (dismissed) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setSubmitting(true);
    try {
      const normalised = email.trim().toLowerCase();
      await axios.post(`${API}/share/classroom/${shareCode}/subscribe`, {
        collection_id: collectionId,
        email: normalised,
      });
      localStorage.setItem(SUBSCRIBED_KEY(collectionId), '1');
      localStorage.setItem(SUBSCRIBED_EMAIL_KEY(collectionId), normalised);
      setDone(true);
      toast.success("You're in — we'll email you when there's something new.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not subscribe — try again');
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY(collectionId), '1');
    setDismissed(true);
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl p-4 mb-6 relative"
      style={{
        background: 'linear-gradient(135deg, rgba(184,151,98,0.10), rgba(194,157,82,0.06))',
        border: '1px solid rgba(194,157,82,0.25)',
      }}
      data-testid="parent-subscribe-prompt"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="parent-subscribe-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="flex items-center gap-2 mb-1.5">
        <BellSimple size={14} weight="fill" className="text-[#C29D52]" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
          Stay in the loop
        </span>
      </div>
      <p className="text-sm text-gray-300 mb-3">
        Pop in your email and we'll let you know when new photos are added — one email a day max,
        no spam, no account needed.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors"
          data-testid="parent-subscribe-email"
        />
        <button
          type="submit"
          disabled={submitting || !email}
          className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:scale-[1.015] disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="parent-subscribe-submit"
        >
          {submitting ? 'Subscribing…' : 'Notify me'}
        </button>
      </div>
    </form>
  );
};
